import React, { useState, useEffect } from 'react';
import {
  purchasesVendorsApi,
  purchasesBillsApi,
  purchasesPaymentsMadeApi,
  type VendorDto,
  type BillDto,
} from '../../services/api';

function formatPeso(amount: number): string {
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

const PAYMENT_METHODS = [
  { value: 'check', label: 'Check' },
  { value: 'wire_transfer', label: 'Wire Transfer' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'other', label: 'Other' },
] as const;

interface RecordPaymentModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function RecordPaymentModal({
  open,
  onClose,
  onSuccess,
}: RecordPaymentModalProps) {
  const [vendors, setVendors] = useState<VendorDto[]>([]);
  const [bills, setBills] = useState<BillDto[]>([]);
  const [vendorId, setVendorId] = useState('');
  const [billId, setBillId] = useState('');
  const [amount, setAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(() =>
    new Date().toISOString().slice(0, 10)
  );
  const [paymentMethod, setPaymentMethod] = useState<string>('check');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      purchasesVendorsApi.list().then((res) => {
        if (res.data) setVendors(res.data);
      });
    }
  }, [open]);

  useEffect(() => {
    if (!open || !vendorId) {
      setBills([]);
      setBillId('');
      setAmount('');
      return;
    }
    purchasesBillsApi.list().then((res) => {
      if (res.data) {
        const openBills = res.data.filter(
          (b) =>
            b.vendor_id === vendorId &&
            b.balance_due > 0 &&
            b.status !== 'paid'
        );
        setBills(openBills);
        setBillId('');
        setAmount('');
      }
    });
  }, [open, vendorId]);

  const selectedBill = bills.find((b) => b.id === billId);
  const maxAmount = selectedBill ? selectedBill.balance_due : undefined;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const amt = parseFloat(amount);
    if (!vendorId.trim()) {
      setError('Please select a vendor.');
      return;
    }
    if (!amount || isNaN(amt) || amt <= 0) {
      setError('Please enter a valid amount.');
      return;
    }
    if (maxAmount != null && amt > maxAmount) {
      setError(`Amount cannot exceed balance due (${formatPeso(maxAmount)}).`);
      return;
    }
    setSaving(true);
    try {
      const res = await purchasesPaymentsMadeApi.create({
        vendor_id: vendorId,
        bill_id: billId || undefined,
        amount: Math.round(amt * 100) / 100,
        payment_date: paymentDate,
        payment_method: paymentMethod,
        reference: reference.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      if (res.errors) throw new Error(res.message || 'Validation failed');
      onSuccess();
      onClose();
      setVendorId('');
      setBillId('');
      setAmount('');
      setReference('');
      setNotes('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-lg max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-xl border border-gray-200"
        role="dialog"
        aria-modal="true"
        aria-labelledby="record-payment-title"
      >
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
          <h2
            id="record-payment-title"
            className="text-xl font-bold text-gray-900"
          >
            Record Payment
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="rounded-xl bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Vendor <span className="text-red-500">*</span>
            </label>
            <select
              value={vendorId}
              onChange={(e) => setVendorId(e.target.value)}
              className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none text-sm"
            >
              <option value="">Select vendor</option>
              {vendors.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.vendor_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Apply to bill (optional)
            </label>
            <select
              value={billId}
              onChange={(e) => {
                setBillId(e.target.value);
                setAmount('');
              }}
              className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none text-sm"
            >
              <option value="">No specific bill</option>
              {bills.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.bill_number} – {formatPeso(b.balance_due)} due
                </option>
              ))}
            </select>
            {bills.length === 0 && vendorId && (
              <p className="mt-1 text-xs text-gray-500">
                No open bills for this vendor.
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Amount (₱) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min={0}
              step={0.01}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none text-sm"
            />
            {maxAmount != null && (
              <p className="mt-1 text-xs text-gray-500">
                Balance due: {formatPeso(maxAmount)}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payment date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payment method
            </label>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none text-sm"
            >
              {PAYMENT_METHODS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reference (e.g. check number, wire reference)
            </label>
            <input
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Check # or transaction reference"
              className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes"
              rows={2}
              className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none text-sm"
            />
          </div>

          <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2.5 text-sm font-semibold text-white bg-brand-600 rounded-lg hover:bg-brand-700 disabled:opacity-60"
            >
              {saving ? 'Saving...' : 'Save Payment'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
